import { RecordAny } from '@dealstack/shared/lib/common/common';
import { Flex, Text } from '@mantine/core';
import React from 'react';
import { CollapsedListCount } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/CollapsedListCount';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';

type Props<T extends RecordAny> = ExtendedColumnDef<T> & {
	headerText: string;
	filterText?: string;
	filterOptions?: { label: string; value: string }[];
	getCohorts: (row: T) => Array<{ id: string; name: string }>;
};

export const createCohortsColumn = <T extends RecordAny>({
	headerText,
	filterText,
	filterOptions,
	getCohorts,
	...columnDef
}: Props<T>): ExtendedColumnDef<T> =>
	createTextColumn<T>({
		headerText,
		columnLabel: headerText,
		filterText: filterText ?? headerText,
		size: 200,
		minSize: 200,
		enableSorting: false,
		showFilter: true,
		filterVariant: 'multi-select',
		...(filterOptions ? { mantineFilterMultiSelectProps: { data: filterOptions } } : {}),
		...columnDef,
		cell: ({ row }) => {
			const cohorts = getCohorts(row.original) ?? [];
			if (cohorts.length === 0) {
				return LONG_DASH;
			}

			const isExpanded = row.getIsExpanded();

			if (isExpanded) {
				return (
					<Flex direction="column" gap="0.25rem" p="0.25rem">
						{cohorts.map((c) => (
							<Text key={c.id} fz="md" c="dark.8">
								{c.name}
							</Text>
						))}
					</Flex>
				);
			}

			const [first, ...rest] = cohorts;
			return (
				<CollapsedListCount
					count={rest.length}
					allowTooltipSummary
					name={first.name}
					tooltipLabel={
						<Flex direction="column" gap="0.25rem" p="0.25rem">
							{rest.map((c) => (
								<Text key={c.id} fz="md" c="dark.8">
									{c.name}
								</Text>
							))}
						</Flex>
					}
				/>
			);
		},
	});
